import { Suspense, useCallback, useMemo, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { Tabs, TabsList, TabsTrigger, TabsContentKeepAlive, usePrefetchTab } from '../../components/Tabs'
import { EntityHeader, EntityHeaderSkeleton } from '../../features/entity/EntityHeader'
import { ProfileSummary } from '../../features/entity/components/ProfileSummary'
import { AliasesChipList } from '../../features/entity/components/AliasesChipList'
import { BasicInfoTable, buildBasicInfoItems } from '../../features/entity/components/BasicInfoTable'
import { SocialMediaGrid } from '../../features/entity/components/SocialMediaGrid'
import { CallerAPIResults } from '../../features/entity/components/CallerAPIResults'
import { EnrichmentStatusBadges } from '../../features/entity/components/EnrichmentStatusBadges'
import { RefreshEnrichmentButton } from '../../features/entity/components/RefreshEnrichmentButton'
import { ConfidenceScore } from '../../features/entity/components/ConfidenceScore'
import { LocationMap } from '../../features/entity/components/LocationMap'
import { AppDataTab } from '../../features/entity/tabs/AppDataTab'
import { PersonalDetailsTab } from '../../features/entity/tabs/PersonalDetailsTab'
import { AccountsTab } from '../../features/entity/tabs/AccountsTab'
import { MapTab } from '../../features/entity/tabs/MapTab'
import { GroupsTab } from '../../features/entity/tabs/GroupsTab'
import { MessagesTab } from '../../features/entity/tabs/MessagesTab'
import { MediaTab } from '../../features/entity/tabs/MediaTab'
import { StatisticsTab } from '../../features/entity/tabs/StatisticsTab'
import { OCRTab } from '../../features/entity/tabs/OCRTab'
import { AISummaryTab } from '../../features/entity/tabs/AISummaryTab'
import { entitySummaryQueryKey } from '../../features/entity/hooks/useEntitySummary'
import { useEntityDetail, entityDetailQueryKey, entityAppDataQueryKey, entityPersonalDetailsQueryKey, entityAccountsQueryKey } from '../../features/entity/hooks/useEntityDetail'
import { entityGroupsQueryKey } from '../../features/entity/hooks/useEntityGroups'
import { entityMessagesQueryKey } from '../../features/entity/hooks/useEntityMessages'
import { entityMediaQueryKey } from '../../features/entity/hooks/useEntityMedia'
import { entityLocationsQueryKey } from '../../features/entity/hooks/useEntityLocations'
import { entityStatisticsQueryKey } from '../../features/entity/hooks/useEntityStatistics'
import { entityOCRQueryKey } from '../../features/entity/hooks/useEntityOCR'
import { EnrichmentModal } from '../../features/entity/components/EnrichmentModal'
import { RelationshipDisplay } from '../../features/entity/components/RelationshipDisplay'
import { LazyEntityMiniGraph } from '../../components/graph/lazy'
import { Skeleton } from '../../components/Skeleton'
import { Button } from '../../components/Button'
import { api } from '../../lib/api'
import { queryClient } from '../../lib/query-client'
import { toastSuccess, toastError } from '../../hooks/useToast'

/* ───── Tab query config ───── */

const TAB_STALE_TIME = 60_000
const TAB_GC_TIME = 300_000

/* ───── Route Definition ───── */

export const Route = createFileRoute('/_authenticated/entities/$id')({
  component: EntityDetailPage,
  loader: ({ params }) => {
    void queryClient.ensureQueryData({
      queryKey: entityDetailQueryKey(params.id),
      queryFn: ({ signal }) =>
        api(`/v1/entities/${params.id}`, { signal }),
      staleTime: 2 * 60 * 1000,
    })
  },
})

/* ───── Page Component ───── */

function EntityDetailPage() {
  const { id } = Route.useParams()
  const { entity, isLoading, error } = useEntityDetail(id)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [enrichmentOpen, setEnrichmentOpen] = useState(false)

  const generateSummaryMutation = useMutation({
    mutationFn: () =>
      api(`/v1/entities/${id}/enrich`, { method: 'POST' }),
    onSuccess: () => {
      toastSuccess('AI summary generation started', {
        description: 'The summary will be available shortly.',
      })
      queryClient.invalidateQueries({ queryKey: entityDetailQueryKey(id) })
    },
    onError: () => {
      toastError('Failed to generate AI summary')
    },
  })

  const handleExport = useCallback(() => {
    if (!entity) return
    const blob = new Blob([JSON.stringify(entity, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `entity-${id}-${new Date().toISOString().slice(0, 10)}.json`
    link.click()
    URL.revokeObjectURL(url)
    toastSuccess('Entity data exported')
  }, [entity, id])

  const prefetchSimpleTab = useCallback(
    (queryKey: readonly unknown[], endpoint: string) => {
      queryClient.prefetchQuery({
        queryKey,
        queryFn: ({ signal }) => api(endpoint, { signal }),
        staleTime: TAB_STALE_TIME,
        gcTime: TAB_GC_TIME,
      })
    },
    [queryClient],
  )

  const prefetchMap = useMemo(
    () => ({
      'app-data': () =>
        prefetchSimpleTab(entityAppDataQueryKey(id), `/v1/entities/${id}/app-data`),
      'personal-details': () =>
        prefetchSimpleTab(entityPersonalDetailsQueryKey(id), `/v1/entities/${id}/personal-details`),
      accounts: () =>
        prefetchSimpleTab(entityAccountsQueryKey(id), `/v1/entities/${id}/accounts`),
      map: () =>
        prefetchSimpleTab(entityLocationsQueryKey(id), `/v1/entities/${id}/locations`),
      groups: () =>
        queryClient.prefetchInfiniteQuery({
          queryKey: entityGroupsQueryKey(id, 'recent_activity'),
          queryFn: ({ signal }) =>
            api(`/v1/entities/${id}/groups`, { params: { sort: 'recent_activity', limit: 30 }, signal }),
          initialPageParam: '' as string,
          staleTime: TAB_STALE_TIME,
          gcTime: TAB_GC_TIME,
        }),
      messages: () =>
        queryClient.prefetchInfiniteQuery({
          queryKey: entityMessagesQueryKey(id, {}),
          queryFn: ({ signal }) =>
            api(`/v1/entities/${id}/messages`, { params: { limit: 20 }, signal }),
          initialPageParam: '' as string,
          staleTime: TAB_STALE_TIME,
          gcTime: TAB_GC_TIME,
        }),
      media: () =>
        queryClient.prefetchInfiniteQuery({
          queryKey: entityMediaQueryKey(id),
          queryFn: ({ signal }) =>
            api(`/v1/entities/${id}/media`, { params: { limit: 20 }, signal }),
          initialPageParam: '' as string,
          staleTime: TAB_STALE_TIME,
          gcTime: TAB_GC_TIME,
        }),
      statistics: () =>
        prefetchSimpleTab(entityStatisticsQueryKey(id), `/v1/entities/${id}/statistics`),
      ocr: () =>
        queryClient.prefetchInfiniteQuery({
          queryKey: entityOCRQueryKey(id),
          queryFn: ({ signal }) =>
            api(`/v1/entities/${id}/ocr`, { params: { limit: 20 }, signal }),
          initialPageParam: '' as string,
          staleTime: TAB_STALE_TIME,
          gcTime: TAB_GC_TIME,
        }),
      'ai-summary': () =>
        prefetchSimpleTab(entitySummaryQueryKey(id), `/v1/entities/${id}/summary`),
    }),
    [id, queryClient, prefetchSimpleTab],
  )

  const { handlers: prefetchHandlers } = usePrefetchTab(prefetchMap)

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <div className="rounded-lg border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/5 p-6 text-center max-w-md">
          <h2 className="text-lg font-semibold text-[var(--color-danger)]">Entity Not Found</h2>
          <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
            The requested entity could not be loaded. It may have been removed or you may not have access.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full" data-testid="entity-detail-page">

      {/* ━━━ Zone 1: Command Bar (breadcrumb + actions) ━━━ */}
      <div className="flex items-center justify-between gap-4 px-6 py-2.5 bg-[var(--color-surface)] border-b border-[var(--color-border)]">
        <nav className="flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)] min-w-0" aria-label="Breadcrumb" data-testid="breadcrumb">
          <Link to="/dashboard" className="hover:text-[var(--color-text-primary)] transition-colors whitespace-nowrap">Home</Link>
          <span className="text-[var(--color-text-muted)]" aria-hidden="true">/</span>
          <Link to="/entities" className="hover:text-[var(--color-text-primary)] transition-colors whitespace-nowrap">Entities</Link>
          <span className="text-[var(--color-text-muted)]" aria-hidden="true">/</span>
          <span className="text-[var(--color-text-primary)] font-medium truncate max-w-[200px]">
            {entity?.name ?? id}
          </span>
        </nav>
        {!isLoading && entity && (
          <div className="flex items-center gap-1.5 shrink-0">
            <Button
              variant="secondary"
              size="sm"
              loading={generateSummaryMutation.isPending}
              disabled={generateSummaryMutation.isPending}
              onClick={() => generateSummaryMutation.mutate()}
              data-testid="generate-summary-button"
            >
              <svg className="h-3.5 w-3.5 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
              {generateSummaryMutation.isPending ? 'Generating...' : 'Summary'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEnrichmentOpen(true)}
              data-testid="enrichment-button"
            >
              <svg className="h-3.5 w-3.5 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3v18" />
                <path d="M17 8l-5-5-5 5" />
                <path d="M3 21h18" />
              </svg>
              Enrich
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExport}
              data-testid="entity-export-button"
            >
              <svg className="h-3.5 w-3.5 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Export
            </Button>
            <Link to="/profiler/$id" params={{ id }} data-testid="generate-profile-button">
              <Button variant="secondary" size="sm" type="button">
                <svg className="h-3.5 w-3.5 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                Profile
              </Button>
            </Link>
            <div className="w-px h-5 bg-[var(--color-border)] mx-0.5" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate({ to: '/investigations', search: { entity: id } })}
              data-testid="open-investigation-button"
            >
              <svg className="h-3.5 w-3.5 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="5" r="3" />
                <circle cx="5" cy="19" r="3" />
                <circle cx="19" cy="19" r="3" />
                <line x1="12" x2="5" y1="8" y2="16" />
                <line x1="12" x2="19" y1="8" y2="16" />
              </svg>
              Investigate
            </Button>
          </div>
        )}
      </div>

      {/* ━━━ Zone 2: Profile Card (header + summary + graph) ━━━ */}
      <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)]">
        {isLoading || !entity ? (
          <EntityHeaderSkeleton />
        ) : (
          <>
            {/* Header row: avatar + info on left, mini-graph on right */}
            <div className="flex gap-6 px-6 pt-5 pb-3">
              <div className="flex-1 min-w-0">
                <EntityHeader entity={entity} />
              </div>
              <div className="shrink-0 hidden lg:flex flex-col items-end gap-2" data-testid="entity-header-mini-graph">
                <Suspense fallback={<Skeleton className="w-[320px] h-[200px] rounded-lg" />}>
                  <LazyEntityMiniGraph
                    entityId={id}
                    width={320}
                    height={200}
                    interactive={false}
                    showLabels={false}
                    className="rounded-lg border border-[var(--color-border)]"
                  />
                </Suspense>
              </div>
            </div>

            {/* Profile summary + enrichment status bar */}
            <div className="px-6 pb-4">
              <div className="flex items-start gap-6">
                <div className="flex-1 min-w-0">
                  <ProfileSummary entityId={id} />
                </div>
              </div>
              <div className="flex items-center justify-between gap-4 mt-3 pt-3 border-t border-[var(--color-border-light)] flex-wrap">
                <div className="flex items-center gap-3 flex-wrap">
                  <EnrichmentStatusBadges entityId={id} />
                  {(entity as Record<string, unknown>).confidence_score !== undefined && (
                    <ConfidenceScore
                      score={(entity as Record<string, unknown>).confidence_score as number}
                      size="md"
                    />
                  )}
                </div>
                <RefreshEnrichmentButton entityId={id} />
              </div>
            </div>
          </>
        )}
      </div>

      {/* ━━━ Zone 3: Intelligence Tabs ━━━ */}
      <div className="flex-1 overflow-auto" data-testid="responsive-container">
        <Tabs defaultValue="app-data" className="h-full">
          <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-6">
            <TabsList>
              <TabsTrigger value="app-data" onMouseEnter={prefetchHandlers['app-data']}>App Data</TabsTrigger>
              <TabsTrigger value="personal-details" onMouseEnter={prefetchHandlers['personal-details']}>Personal Details</TabsTrigger>
              <TabsTrigger value="accounts" onMouseEnter={prefetchHandlers['accounts']}>Accounts / Profiler</TabsTrigger>
              <TabsTrigger value="map" onMouseEnter={prefetchHandlers['map']}>Map</TabsTrigger>
              <TabsTrigger value="groups" onMouseEnter={prefetchHandlers['groups']}>Groups</TabsTrigger>
              <TabsTrigger value="messages" onMouseEnter={prefetchHandlers['messages']}>Messages</TabsTrigger>
              <TabsTrigger value="media" onMouseEnter={prefetchHandlers['media']}>Media</TabsTrigger>
              <TabsTrigger value="statistics" onMouseEnter={prefetchHandlers['statistics']}>Statistics</TabsTrigger>
              <TabsTrigger value="ocr" onMouseEnter={prefetchHandlers['ocr']}>OCR</TabsTrigger>
              <TabsTrigger value="ai-summary" onMouseEnter={prefetchHandlers['ai-summary']}>AI Summary</TabsTrigger>
              <TabsTrigger value="network">Network</TabsTrigger>
            </TabsList>
          </div>

          <div className="px-6 py-4">
            <TabsContentKeepAlive value="app-data">
              <AppDataTab entityId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="personal-details">
              <PersonalDetailsTab entityId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="accounts">
              {entity && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-[var(--color-text-heading)] mb-3">Known Aliases</h3>
                  <AliasesChipList aliases={(entity as Record<string, unknown>).aliases as string[] ?? []} />
                </div>
              )}
              {entity && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-[var(--color-text-heading)] mb-3">Basic Information</h3>
                  <BasicInfoTable
                    items={buildBasicInfoItems({
                      phone_numbers: (entity as Record<string, unknown>).phone_numbers as string[] | undefined,
                      emails: (entity as Record<string, unknown>).emails as string[] | undefined,
                      country: entity.country,
                      language: (entity as Record<string, unknown>).language as string | undefined,
                      first_seen: (entity as Record<string, unknown>).first_seen as string | undefined,
                      last_seen: entity.last_active,
                    })}
                  />
                </div>
              )}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-[var(--color-text-heading)] mb-3">CallerAPI Results</h3>
                <CallerAPIResults entityId={id} />
              </div>
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-[var(--color-text-heading)] mb-3">Social Media Accounts</h3>
                <SocialMediaGrid entityId={id} />
              </div>
              <AccountsTab entityId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="map">
              <LocationMap entityId={id} className="mb-6" />
              <MapTab entityId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="groups">
              <GroupsTab entityId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="messages">
              <MessagesTab entityId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="media">
              <MediaTab entityId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="statistics">
              <StatisticsTab entityId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="ocr">
              <OCRTab entityId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="ai-summary">
              <AISummaryTab entityId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="network">
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-[var(--color-text-heading)] mb-3">Relationships</h3>
                <RelationshipDisplay entityId={id} maxItems={15} />
              </div>
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-[var(--color-text-heading)] mb-3">Network Graph</h3>
                <Suspense fallback={<Skeleton className="w-full h-[400px] rounded-lg" />}>
                  <LazyEntityMiniGraph
                    entityId={id}
                    width={undefined}
                    height={400}
                    interactive
                    showLabels={false}
                    className="w-full"
                  />
                </Suspense>
              </div>
            </TabsContentKeepAlive>
          </div>
        </Tabs>
      </div>

      <EnrichmentModal
        entityId={id}
        open={enrichmentOpen}
        onClose={() => setEnrichmentOpen(false)}
      />
    </div>
  )
}
